import React, { useState, useMemo, useEffect } from 'react';
import { BsReceipt, BsDownload, BsCashStack } from 'react-icons/bs';
import { useCustomer } from './CustomerContext';
import StatusBadge from './components/StatusBadge';
import EmptyState from './components/EmptyState';
import Pagination from './components/Pagination';
import { formatDate, formatEur, apiFetch } from '../../utils/api';
const PER_PAGE = 20;

const PENDING_STATES = ['pendiente', 'parcial', 'validada'];
const isPaid = (i) => {
  const sp = String(i.stripe_payment_status || '').toLowerCase();
  if (sp === 'paid' || sp === 'succeeded' || sp === 'pagado') return true;
  return String(i.estado || '').toLowerCase() === 'pagada';
};

const CustomerInvoicesPage = () => {
  const { invoices, loading, authHeaders } = useCustomer();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState('');

  const downloadPdf = async (inv) => {
    setDownloadingId(inv.id);
    setError('');
    try {
      const res = await apiFetch(`/api/v1/software/customer/portal/invoices/${inv.id}/pdf`, { headers: authHeaders() });
      if (!res.ok) { setError('No se pudo generar el PDF de la factura.'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(inv.numero || `factura-${inv.id}`).replace(/[^a-zA-Z0-9._-]+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('No se pudo descargar el PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  const filtered = useMemo(() => {
    let list = invoices;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i => (i.numero || `#${i.id}`).toLowerCase().includes(q));
    }
    if (statusFilter) {
      list = list.filter(i => String(i.estado || '').toLowerCase() === statusFilter.toLowerCase());
    }
    return list;
  }, [invoices, search, statusFilter]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    setPage(p => Math.min(p, totalPages));
  }, [filtered.length]);

  const totals = useMemo(() => {
    let billed = 0;
    let pending = 0;
    for (const i of invoices) {
      // Las proformas no son facturas fiscales: no suman a facturado ni a pendiente.
      if (i.invoice_type === 'proforma') continue;
      const total = Number(i.total) || 0;
      billed += total;
      if (PENDING_STATES.includes(String(i.estado || '').toLowerCase())) pending += total;
    }
    return { billed, pending };
  }, [invoices]);

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const statuses = [...new Set(invoices.map(i => i.estado).filter(Boolean))];

  if (loading) {
    return (
      <div>
        <div className="cp-skeleton cp-skeleton-title" />
        <div className="cp-skeleton cp-skeleton-card" style={{ height: 300 }} />
      </div>
    );
  }

  return (
    <div>
      <h1 className="cp-page-title">Facturas</h1>

      {error && <div className="cp-alert cp-alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      {!invoices.length ? (
        <EmptyState icon={BsReceipt} title="Sin facturas" subtitle="Aún no tienes facturas registradas." />
      ) : (
        <>
          {totals.pending > 0 && (
            <div className="cp-stats-grid" style={{ marginBottom: 16 }}>
              <div className="cp-stat-card orange">
                <div className="cp-stat-icon"><BsCashStack /></div>
                <div className="cp-stat-label">Pendiente de pago</div>
                <div className="cp-stat-value">{formatEur(totals.pending)}</div>
              </div>
            </div>
          )}

          <div className="cp-filter-bar">
            <input
              className="cp-search-input"
              placeholder="Buscar por numero..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
            <select className="cp-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">Todos los estados</option>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="cp-card-static">
            <div style={{ overflowX: 'auto' }}>
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Numero</th>
                    <th>Fecha</th>
                    <th style={{ textAlign: 'right' }}>Subtotal</th>
                    <th style={{ textAlign: 'right' }}>IVA</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th>Estado</th>
                    <th className="cp-th-pdf"><span style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>PDF</span></th>
                  </tr>
                </thead>
                <tbody>
                  {!paged.length ? (
                    <tr><td colSpan="7"><EmptyState icon={BsReceipt} title="Sin resultados" subtitle="Prueba con otros filtros." /></td></tr>
                  ) : paged.map(i => {
                    const paid = isPaid(i);
                    return (
                      <tr key={i.id}>
                        <td style={{ fontWeight: 500 }}>{i.numero || `#${i.id}`}</td>
                        <td>{formatDate(i.fecha)}</td>
                        <td style={{ textAlign: 'right' }}>{formatEur(i.subtotal)}</td>
                        <td style={{ textAlign: 'right' }}>{formatEur(i.iva)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatEur(i.total)}</td>
                        <td>
                          <StatusBadge status={paid ? 'Pagada' : i.estado} />
                          {i.invoice_type === 'proforma' && (
                            <span className="cp-badge cp-badge-pending" style={{ marginLeft: 6 }}>Proforma</span>
                          )}
                        </td>
                        <td className="cp-td-pdf">
                          <button
                            type="button"
                            className="cp-btn-pdf"
                            onClick={() => downloadPdf(i)}
                            disabled={downloadingId === i.id}
                            title="Descargar PDF"
                            aria-label={`Descargar PDF de ${i.numero || `factura ${i.id}`}`}
                          >
                            <BsDownload /> {downloadingId === i.id ? '...' : 'PDF'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '0 16px' }}>
              <Pagination total={filtered.length} page={page} perPage={PER_PAGE} onPageChange={setPage} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerInvoicesPage;
